import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowRight,
  Mail,
  Calendar,
  Mic,
  Github,
  CheckCircle2,
  ExternalLink,
  Sparkles,
  Rocket,
  Check,
  MessageCircle,
  Loader2,
  Send,
  Eye,
  EyeOff,
  Headphones,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useCommandStyle } from "@/contexts/CommandStyleContext";
import { COMMAND_STYLES, type CommandStyleId } from "@/lib/command-styles";
import { useIntegrations, useUpsertIntegration, useSyncIntegration } from "@/hooks/useIntegrations";
import { useGmailConnect } from "@/hooks/useEmails";
import { useGitHubConnect } from "@/hooks/useGitHub";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { formatError } from "@/lib/utils";

const STORAGE_KEY = "ho-onboarding-complete";
// Types we know how to sync immediately after OAuth lands.
const SYNCABLE_TYPES = ["gmail", "fireflies", "plaud", "beeper"] as const;
type SyncableType = (typeof SYNCABLE_TYPES)[number];
function isSyncable(t: string): t is SyncableType {
  return (SYNCABLE_TYPES as readonly string[]).includes(t);
}

type OnboardingWizardProps = {
  onDismiss: () => void;
};

const slideVariants = {
  enter: (direction: number) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction > 0 ? -300 : 300, opacity: 0 }),
};

// ---------- IntegrationCard (inline Connect + collapsible paste-token form) ----------

type IntegrationCardProps = {
  icon: React.ReactNode;
  name: string;
  description: string;
  connected: boolean;
  // Either a redirect-style OAuth handler OR a paste-token inline panel.
  onConnect?: () => void | Promise<void>;
  pastePanel?: React.ReactNode;
  busy?: boolean;
};

function IntegrationCard({
  icon,
  name,
  description,
  connected,
  onConnect,
  pastePanel,
  busy,
}: IntegrationCardProps) {
  const [showPaste, setShowPaste] = useState(false);

  return (
    <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm">
      <div className="flex items-center gap-4 p-4">
        <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-muted shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{name}</span>
            {connected && <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
        {!connected && (onConnect || pastePanel) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (pastePanel) {
                setShowPaste((v) => !v);
              } else if (onConnect) {
                onConnect();
              }
            }}
            disabled={busy}
            className="shrink-0 text-xs gap-1.5"
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                Connect
                {!pastePanel && <ExternalLink className="h-3 w-3" />}
              </>
            )}
          </Button>
        )}
        {connected && <span className="text-xs text-green-500 font-medium shrink-0">Connected</span>}
      </div>
      {showPaste && !connected && pastePanel && (
        <div className="px-4 pb-4 pt-1 border-t border-border">{pastePanel}</div>
      )}
    </div>
  );
}

// ---------- Paste-token panels ----------

function FirefliesPanel({ onConnected }: { onConnected: () => void }) {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const upsert = useUpsertIntegration();

  const handleSubmit = async () => {
    if (!apiKey.trim()) return;
    try {
      await upsert.mutateAsync({
        type: "fireflies",
        config: { api_key: apiKey.trim() },
        enabled: true,
      });
      toast.success("Fireflies connected");
      onConnected();
    } catch (err) {
      toast.error(`Connect failed: ${formatError(err)}`);
    }
  };

  return (
    <div className="space-y-2 mt-2">
      <Label className="text-xs">API key (fireflies.ai/integrations)</Label>
      <div className="flex items-center gap-2">
        <Input
          type={showKey ? "text" : "password"}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="..."
          className="font-mono text-xs"
        />
        <Button size="icon" variant="outline" onClick={() => setShowKey((v) => !v)}>
          {showKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </Button>
      </div>
      <Button onClick={handleSubmit} disabled={!apiKey.trim() || upsert.isPending} size="sm" className="w-full">
        {upsert.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
        Save
      </Button>
    </div>
  );
}

function BeeperPanel({ onConnected }: { onConnected: () => void }) {
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const upsert = useUpsertIntegration();

  const handleSubmit = async () => {
    if (!token.trim()) return;
    try {
      await upsert.mutateAsync({
        type: "beeper",
        config: {
          access_token: token.trim(),
          homeserver_url: "https://matrix.beeper.com",
          since_token: null,
        },
        enabled: true,
      });
      toast.success("Beeper connected — first sync queued");
      onConnected();
    } catch (err) {
      toast.error(`Connect failed: ${formatError(err)}`);
    }
  };

  return (
    <div className="space-y-2 mt-2">
      <Label className="text-xs">Beeper access token (beeper.com/account)</Label>
      <div className="flex items-center gap-2">
        <Input
          type={showToken ? "text" : "password"}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="syt_..."
          className="font-mono text-xs"
        />
        <Button size="icon" variant="outline" onClick={() => setShowToken((v) => !v)}>
          {showToken ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </Button>
      </div>
      <Button onClick={handleSubmit} disabled={!token.trim() || upsert.isPending} size="sm" className="w-full">
        {upsert.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
        Save
      </Button>
    </div>
  );
}

// ---------- AI chat step ----------

type ChatTurn = { role: "user" | "assistant"; content: string };

function ChatStep({ onContinue }: { onContinue: () => void }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState("");
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [busy, setBusy] = useState(false);

  const suggested = [
    "What should I focus on today?",
    "Summarize my unread emails",
    "Help me triage my inbox",
  ];

  const send = async (text: string) => {
    if (!user || !text.trim() || busy) return;
    const userTurn: ChatTurn = { role: "user", content: text };
    setTurns((t) => [...t, userTurn]);
    setBusy(true);
    setDraft("");
    try {
      const { data, error } = await supabase.functions.invoke("chat", {
        body: {
          messages: [...turns, userTurn].map((t) => ({ role: t.role, content: t.content })),
          user_id: user.id,
        },
      });
      if (error) throw error;
      const reply: ChatTurn = {
        role: "assistant",
        content: (data && (data as { response?: string }).response) || "I couldn't process that. Try again.",
      };
      setTurns((t) => [...t, reply]);
    } catch (err) {
      setTurns((t) => [...t, { role: "assistant", content: `Error: ${formatError(err)}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-2">ASK ME ANYTHING</h2>
        <p className="text-sm text-muted-foreground">
          Your command center has an AI co-pilot. Try it. It already knows about your tasks, calendar, and inbox.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card/50 mb-4 min-h-[180px] max-h-[300px] overflow-y-auto p-3 space-y-2">
        {turns.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">
            Click a suggestion below or type your own question.
          </p>
        ) : (
          turns.map((t, i) => (
            <div
              key={i}
              className={`text-xs px-3 py-2 rounded-lg ${
                t.role === "user"
                  ? "bg-primary/15 ml-6"
                  : "bg-muted mr-6"
              }`}
            >
              {t.content}
            </div>
          ))
        )}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-1 mr-6">
            <Loader2 className="h-3 w-3 animate-spin" />
            Thinking…
          </div>
        )}
      </div>

      {turns.length === 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {suggested.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => send(s)}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 transition-colors disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-6">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(draft);
            }
          }}
          placeholder="Type a question…"
          disabled={busy}
          className="text-sm"
        />
        <Button onClick={() => send(draft)} disabled={!draft.trim() || busy} size="icon">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex justify-center">
        <Button onClick={onContinue} size="lg" className="gap-2 px-8">
          {turns.length === 0 ? "SKIP" : "DONE"}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ---------- Main wizard ----------

const MAX_STEP = 4;

export function OnboardingWizard({ onDismiss }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const navigate = useNavigate();
  const { styleId, setStyle, copy } = useCommandStyle();
  const [pickedStyle, setPickedStyle] = useState<CommandStyleId | null>(null);

  const { data: integrations } = useIntegrations();
  const isConnected = (type: string) => !!integrations?.some((i) => i.type === type && i.enabled);
  const calendarConnected = isConnected("gmail") || isConnected("google_calendar");

  const gmailConnect = useGmailConnect();
  const githubConnect = useGitHubConnect();
  const sync = useSyncIntegration();
  const [busyType, setBusyType] = useState<string | null>(null);

  // Auto-fire one-time sync for any integration we haven't yet kicked off in
  // this session. Drives Workstream E E3 — "see classified items within ~30s,
  // not waiting for next cron tick".
  const syncedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!integrations) return;
    for (const integ of integrations) {
      if (!integ.enabled) continue;
      if (!isSyncable(integ.type)) continue;
      if (integ.last_synced_at) continue; // cron / earlier kick already handled it
      const key = `${integ.type}:${integ.id}`;
      if (syncedRef.current.has(key)) continue;
      syncedRef.current.add(key);
      sync.mutate(integ.type, {
        onSuccess: (r) => {
          const result = r as { messages?: number; created?: number } | null;
          const count = result?.messages ?? result?.created;
          if (typeof count === "number" && count > 0) {
            toast.success(`${integ.type}: ${count} item${count === 1 ? "" : "s"} pulled in`);
          }
        },
        onError: () => {/* silent — surface in Settings */},
      });
    }
  }, [integrations, sync]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    onDismiss();
  };

  const goNext = () => {
    setDirection(1);
    setStep((s) => Math.min(s + 1, MAX_STEP));
  };

  const connectGmail = async () => {
    try {
      setBusyType("gmail");
      const r = await gmailConnect.mutateAsync();
      window.location.href = r.url;
    } catch (e) {
      toast.error(`Gmail connect failed: ${String(e)}`);
      setBusyType(null);
    }
  };

  const connectGitHub = async () => {
    try {
      setBusyType("github");
      const r = await githubConnect.mutateAsync(undefined);
      window.location.href = r.url;
    } catch (e) {
      toast.error(`GitHub connect failed: ${String(e)}`);
      setBusyType(null);
    }
  };

  const connectPlaud = () => {
    // Plaud OAuth — same redirect pattern as Settings PlaudCard.
    const clientId = "client_a1383c4a-468b-4f6a-b706-8b8e5fa56218";
    const redirectUri = "https://nnqsytkgfaodqqmtectv.supabase.co/functions/v1/plaud-callback";
    window.location.href = `https://app.plaud.ai/platform/oauth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code`;
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        onClick={handleDismiss}
        className="absolute right-6 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        style={{ top: "max(1.5rem, env(safe-area-inset-top))" }}
        aria-label="Close onboarding"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="absolute left-1/2 -translate-x-1/2 flex gap-2" style={{ top: "max(1.5rem, env(safe-area-inset-top))" }}>
        {Array.from({ length: MAX_STEP + 1 }, (_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === step ? "w-8 bg-primary" : i < step ? "w-1.5 bg-primary/50" : "w-1.5 bg-muted-foreground/20"
            }`}
          />
        ))}
      </div>

      <div className="w-full max-w-lg px-6">
        <AnimatePresence mode="wait" custom={direction}>
          {step === 0 && (
            <motion.div
              key="step-0"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col items-center text-center"
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
                className="mb-8"
              >
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 mx-auto">
                  <Sparkles className="h-10 w-10 text-primary" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight mb-3">Command Center Online.</h1>
                <p className="text-lg text-muted-foreground max-w-sm">
                  Everything you run — in one place. Let's wire it up.
                </p>
              </motion.div>

              <Button onClick={goNext} size="lg" className="gap-2 px-8">
                LET'S GO
                <ArrowRight className="h-4 w-4" />
              </Button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step-1"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold tracking-tight mb-2">PICK YOUR OPERATING STYLE</h2>
                <p className="text-sm text-muted-foreground">
                  This sets the tone for how your command center feels and talks to you.
                </p>
              </div>

              <div className="flex flex-col gap-3 mb-8">
                {(["operator", "architect", "wave"] as CommandStyleId[]).map((id) => {
                  const s = COMMAND_STYLES[id];
                  const isSelected = (pickedStyle || styleId) === id;
                  return (
                    <button
                      key={id}
                      onClick={() => {
                        setPickedStyle(id);
                        setStyle(id);
                      }}
                      className="relative text-left rounded-xl border-2 p-4 transition-all duration-200"
                      style={{
                        borderColor: isSelected ? s.colors.primary : s.colors.border,
                        backgroundColor: s.colors.surface,
                        boxShadow: isSelected ? `0 0 20px ${s.colors.primary}30` : "none",
                      }}
                    >
                      {isSelected && (
                        <div
                          className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: s.colors.primary }}
                        >
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                      <h3
                        className="text-base font-bold tracking-tight mb-0.5"
                        style={{ color: s.colors.textPrimary }}
                      >
                        {s.name}
                      </h3>
                      <p className="text-xs mb-2" style={{ color: s.colors.textSecondary }}>
                        {s.identity}
                      </p>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full border border-white/10"
                          style={{ backgroundColor: s.colors.background }}
                        />
                        <div
                          className="w-4 h-4 rounded-full border border-white/10"
                          style={{ backgroundColor: s.colors.primary }}
                        />
                        <div
                          className="w-4 h-4 rounded-full border border-white/10"
                          style={{ backgroundColor: s.colors.surface }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-center">
                <Button onClick={goNext} size="lg" className="gap-2 px-8" disabled={!pickedStyle && !styleId}>
                  NEXT
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step-2"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold tracking-tight mb-2">PLUG IN YOUR WORLD</h2>
                <p className="text-sm text-muted-foreground">
                  We pull from everywhere so you never miss a move.
                </p>
              </div>

              <div className="flex flex-col gap-3 mb-6 max-h-[55vh] overflow-y-auto">
                <IntegrationCard
                  icon={<Mail className="h-5 w-5 text-blue-500" />}
                  name="Gmail"
                  description="Import actionable emails as tasks (auto-classified)"
                  connected={isConnected("gmail")}
                  onConnect={connectGmail}
                  busy={busyType === "gmail"}
                />
                <IntegrationCard
                  icon={<Calendar className="h-5 w-5 text-blue-500" />}
                  name="Google Calendar"
                  description="Bundled with the Gmail OAuth grant"
                  connected={calendarConnected}
                />
                <IntegrationCard
                  icon={<MessageCircle className="h-5 w-5 text-cyan-500" />}
                  name="Beeper"
                  description="iMessage / SMS / WhatsApp / Signal — no Mac required"
                  connected={isConnected("beeper")}
                  pastePanel={<BeeperPanel onConnected={() => {/* upsert invalidates query */}} />}
                />
                <IntegrationCard
                  icon={<Mic className="h-5 w-5 text-orange-500" />}
                  name="Fireflies"
                  description="Turn meeting notes into action items"
                  connected={isConnected("fireflies")}
                  pastePanel={<FirefliesPanel onConnected={() => {/* upsert invalidates query */}} />}
                />
                <IntegrationCard
                  icon={<Headphones className="h-5 w-5 text-purple-500" />}
                  name="Plaud"
                  description="Capture voice memos as tasks"
                  connected={isConnected("plaud")}
                  onConnect={connectPlaud}
                />
                <IntegrationCard
                  icon={<Github className="h-5 w-5 text-foreground" />}
                  name="GitHub"
                  description="Track issues and PRs across your repos"
                  connected={isConnected("github")}
                  onConnect={connectGitHub}
                  busy={busyType === "github"}
                />
              </div>

              <div className="flex items-center gap-3 justify-center">
                <Button variant="ghost" onClick={goNext} className="text-muted-foreground">
                  Skip for now
                </Button>
                <Button onClick={goNext} className="gap-2">
                  NEXT
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step-3"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ChatStep onContinue={goNext} />
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="step-4"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex flex-col items-center text-center"
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
                className="mb-8"
              >
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 mx-auto">
                  <Rocket className="h-10 w-10 text-primary" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight mb-3">YOU'RE LOCKED IN.</h1>
                <p className="text-muted-foreground max-w-sm mb-2">
                  Tasks, emails, calendar — AI handles the noise. You make the calls.
                </p>
              </motion.div>

              <Button onClick={handleDismiss} size="lg" className="gap-2 px-8">
                {copy.onboardingFinalButton}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <button
                type="button"
                onClick={() => navigate("/settings")}
                className="mt-4 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                Fine-tune anything in Settings →
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
